import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconDownload, IconSearch, IconX } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getStockOnHand } from "api/report.service";
import { navigateTo } from "common/navigation";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import ProductCodeInput from "pages/admin/components/ProductCodeInput";
import React, { useEffect, useMemo, useState } from "react";
import { RptSoH, RptSoHs } from "types/model.types";

const AdminSoHReportPage: React.FC = () => {
  const [products, setProducts] = useState<RptSoHs>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState(1);
  // Inside your component:
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [fromCode, setFromCode] = useState<number | undefined>();
  const [toCode, setToCode] = useState<number | undefined>();

  const statusOptions = [
    { value: "3", label: "Stock OK" },
    { value: "2", label: "Low Stock" },
    { value: "1", label: "No Stock" },
  ];
  const getLockLevel = (p: RptSoH): string => {
    if (p.onHandCtn <= 0) return "1";
    if (p.onHandCtn <= 10) return "2";
    return "3";
  };

  const fetchData = async (mounted: boolean, downloadCsv: boolean) => {
    try {
      const response = await safeApiCall(
        getStockOnHand(1, 9999, fromCode, toCode, downloadCsv),
      );
      if (mounted && !downloadCsv) setProducts(response.data ?? []);
    } catch (err) {
      setError("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    fetchData(mounted, false);
    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();

    return products.filter((b) => {
      const name = b.description?.toLowerCase() ?? "";
      const code = b.id?.toString().toLowerCase() ?? "";

      let matchesSearch = true;
      if (normalizedSearch.length >= 3) {
        matchesSearch =
          name.includes(normalizedSearch) || code.includes(normalizedSearch);
      }

      let matchesLevel = true;
      if (statusFilter !== null) {
        const level = getLockLevel(b);
        matchesLevel = statusFilter === null || level === statusFilter;
      }

      return matchesSearch && matchesLevel;
    });
  }, [products, searchTerm, statusFilter]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  const rows = paginatedItems.map((p) => {
    let stockLabel = "";

    if (p.onHandCtn <= 0) {
      stockLabel = "No Stock";
    } else if (p.onHandCtn <= 10) {
      stockLabel = "Low Stock";
    } else {
      stockLabel = "Stock OK";
    }

    return (
      <Table.Tr key={p.id}>
        <Table.Td>{p.id}</Table.Td>
        <Table.Td>{p.description}</Table.Td>
        <Table.Td className="money">{p.unitsCtn}</Table.Td>
        <Table.Td className="money">{p.onHand.toFixed(0)}</Table.Td>
        <Table.Td className="money">{p.onHandCtn.toFixed(0)}</Table.Td>
        <Table.Td>
          <Badge
            color={
              p.onHandCtn <= 0 ? "red" : p.onHandCtn <= 10 ? "orange" : "blue"
            }
          >
            {stockLabel}
          </Badge>
        </Table.Td>
      </Table.Tr>
    );
  });
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
    setActivePage(1); // Reset pagination to first page
  };

  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Stock on Hand Report</Title>
        <Button
          onClick={() => {
            navigateTo(
              `/print/stock-on-hand?s=${searchTerm ?? ""}&f=${fromCode ?? ""}&t=${toCode ?? ""}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>

      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : error ? (
        <Alert color="red">{error}</Alert>
      ) : (
        <>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Text size="sm" c="dimmed">
              Showing 30 items per page
            </Text>
            <Pagination
              total={Math.ceil(
                (filteredItems?.length ?? 0) / DEFAULT_PAGE_SIZE,
              )}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
          <Group align="end" gap="sm">
            <ProductCodeInput
              name="fromCode"
              label="From Product Code"
              value={fromCode}
              setValue={setFromCode}
              resetPage={() => {
                setActivePage(1);
              }}
              maxLength={6} // e.g. max 6 digits
              min={0}
              max={999999}
            />
            <ProductCodeInput
              name="toCode"
              label="To Product Code"
              value={toCode}
              setValue={setToCode}
              resetPage={() => {
                setActivePage(1);
              }}
              maxLength={6} // e.g. max 6 digits
              min={0}
              max={999999}
            />
            <Button size={"sm"} onClick={() => fetchData(true, false)}>
              Refresh
            </Button>
            <Button
              size={"sm"}
              onClick={() => {
                setFromCode(undefined);
                setToCode(undefined);
              }}
            >
              Clear Filters
            </Button>
            <Tooltip label="Download CSV">
              <ActionIcon size={36} onClick={() => fetchData(true, true)}>
                <IconDownload size={24} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group justify="flex-end" wrap="nowrap" mb="md" w="100%">
            <TextInput
              placeholder="Search by any title"
              leftSection={<IconSearch size={16} stroke={1.5} />}
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ flex: 1 }} // ← makes the input expand fully
              rightSection={
                searchTerm.length > 0 ? (
                  <ActionIcon onClick={() => setSearchTerm("")} size="sm">
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
            />
          </Group>

          <Table striped highlightOnHover withTableBorder w={"100%"}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Desscription</Table.Th>
                <Table.Th className="money">Units / Ctn</Table.Th>
                <Table.Th className="money">SoH (Units)</Table.Th>
                <Table.Th className="money">SoH (CTN)</Table.Th>
                <Table.Th>
                  <Select
                    placeholder="All"
                    data={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    clearable
                    mb="md"
                  />
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(
                (filteredItems?.length ?? 0) / DEFAULT_PAGE_SIZE,
              )}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
    </Stack>
  );
};

export default AdminSoHReportPage;
